import { ReactNode, useState } from 'react'
import styled from 'styled-components'
import { useAutoAnimate } from '@formkit/auto-animate/react'

import DottedLine from '../icons/DottedLine'
import { media } from '../../utils/style-utils'

interface IAccordion {
	heading: string
	children: ReactNode
	className?: string
	onClick?: () => void
	isExpanded?: boolean
	textContent?: string
}

const Accordion = ({ className, children, heading, onClick, isExpanded }: IAccordion) => {
	const [localExpand, setLocalExpand] = useState(false)
	const [parent] = useAutoAnimate<HTMLDivElement>({ duration: 500 })
	const toggle = () => setLocalExpand(!localExpand)

	const expanded = isExpanded ?? localExpand

	return (
		<Container
			ref={parent}
			className={className}
			tabIndex={0}
			role='button'
			aria-expanded={expanded}
			onClick={() => {
				toggle()
				onClick && onClick()
			}}
			onKeyDown={({ key, code }) => {
				if (key !== 'Enter' && code !== 'Space') return
				toggle()
				onClick && onClick()
			}}
		>
			<SubContainer>
				<Question>{heading}</Question>
				<div>
					<HorizontalLine
						fill={'var(--orange)'}
						expanded={expanded ?? undefined}
					/>
					<VerticalLine
						fill={'var(--orange)'}
						expanded={expanded ?? undefined}
					/>
				</div>
			</SubContainer>
			{expanded && (
				<ChildrenContainer>
					<div>{children}</div>
				</ChildrenContainer>
			)}
		</Container>
	)
}

const HorizontalLine = styled(DottedLine)<{ expanded: boolean }>`
	transform: rotate(${({ expanded }) => (expanded ? '180deg' : '0deg')});
	transition-duration: 0.75s;
	position: absolute;
`
const VerticalLine = styled(DottedLine)<{ expanded: boolean }>`
	transform: rotate(${({ expanded }) => (expanded ? '180deg' : '-90deg')});
	transition-duration: 0.75s;
	position: absolute;
`

const Question = styled.h4`
	line-height: 2.25rem;
	font-weight: 400;
	font-size: 1.75rem;
	color: var(--deep-blue);
	margin-bottom: var(--spacer-xs);
	margin-top: var(--spacer-md);

	${media.phone`
		font-size: 1.25rem;
		line-height: 1.5rem;
		margin-bottom: var(--spacer-2xs);
		margin-top: var(--spacer-xs);
	`}

	${media.desktopHuge`
		font-size: 3.4rem;	
		line-height: 4rem;
	`}
`

const SubContainer = styled.div`
	cursor: pointer;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	gap: var(--spacer-xs);
`

const Container = styled.div`
	padding-right: 24px;
	display: flex;
	flex-direction: column;
	margin-bottom: 20px;
	&:focus-visible {
		outline: 3px solid var(--orange);
		outline-offset: 1px;
		border-radius: 32px;
	}
`

const ChildrenContainer = styled.div`
	* {
		font-style: normal;
		font-weight: 300;
		font-size: 1.25rem;
		color: var(--deep-blue);

		${media.desktopHuge`
			font-size: 2.8rem;	
			line-height: 3rem;
		`}
	}
`

export default Accordion
